import { Button } from "@/components/ui/shadcn/Button/Button";
import { getNotificationVisual } from "@/components/ui/NotificationItems/notificationVisual";
import { getSmsReplyTarget } from "@/components/ui/NotificationItems/notificationCategories";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { searchMattersQueryOptions } from "@/lib/data/react-query/matters/search-matters";
import { useUpdateNotificationInboxReadState } from "@/lib/data/react-query/matters/matter-notification";
import type { NotificationInboxItem } from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef } from "react";
import toast from "react-hot-toast";

const NotificationToastMessage = ({
  notification,
  onOpen,
  onReply,
}: {
  notification: NotificationInboxItem;
  onOpen?: () => void;
  onReply?: () => void;
}) => {
  // Share the inbox's title lookup. Fetching matter details would mark it viewed.
  const matterId = notification.destination?.matterId;
  const { data: mattersData } = useQuery({
    ...searchMattersQueryOptions({
      matterIds: matterId ? [matterId] : [],
    }),
    enabled: Boolean(matterId),
  });
  const matterTitle =
    mattersData?.data.find((matter) => matter.id === matterId)?.title ??
    "Matter";
  const preview = notification.body.preview?.trim();
  const details = notification.body.details
    .map((detail) => `${detail.label}: ${detail.value}`)
    .join(" · ");

  return (
    <div
      className="flex w-full min-w-0 items-start gap-2 text-left"
      data-preserve-open-modal="true"
    >
      <div className="text-foreground min-w-0 flex-1 text-left">
        <span className="line-clamp-2 text-base leading-5 font-semibold group-hover:underline">
          {onOpen ? (
            <button
              type="button"
              title="Open matter"
              onClick={onOpen}
              className="text-left hover:underline"
            >
              {notification.title}
            </button>
          ) : (
            notification.title
          )}
        </span>
        {matterId && preview && (
          <span className="mt-1 line-clamp-2 text-sm leading-5 break-words">
            {preview}
          </span>
        )}
        {matterId && details && (
          <span className="text-muted-foreground mt-0.5 line-clamp-1 text-sm leading-5">
            {details}
          </span>
        )}
        {matterId ? (
          <span className="text-muted-foreground mt-1 block max-w-full truncate text-sm leading-5">
            {matterTitle}
          </span>
        ) : null}
      </div>
      {onReply ? (
        <Button type="button" variant="outline" size="sm" onClick={onReply}>
          Reply
        </Button>
      ) : null}
    </div>
  );
};

export const useLiveNotificationToasts = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate: markNotificationRead } =
    useUpdateNotificationInboxReadState();
  const { openCommunicationModal } = useGlobalModal();
  const { hasActionPermission } = useUser();
  const canSendSms = hasActionPermission(
    ACTION_PERMISSION_KEYS.contact,
    ACTION_PERMISSION_VALUES.create,
  );
  const toastIds = useRef<string[]>([]);

  useEffect(
    () => () => {
      toastIds.current.forEach((id) => {
        toast.remove(id);
      });
      toastIds.current = [];
    },
    [],
  );

  return useCallback(
    (notifications: NotificationInboxItem[]) => {
      notifications.forEach((notification) => {
        const id = `live-notification:${notification.id}`;
        if (toastIds.current.includes(id)) return;
        toastIds.current.push(id);
        while (toastIds.current.length > 3) {
          const oldest = toastIds.current.shift();
          if (oldest) toast.remove(oldest);
        }
        const visual = getNotificationVisual(notification.eventType);
        const Icon = visual.Icon;
        const replyTarget = canSendSms ? getSmsReplyTarget(notification) : null;
        const matterId = notification.destination?.matterId;

        // Use the standard ToastBar so the app's shared Toaster owns appearance.
        toast(
          () => (
            <NotificationToastMessage
              notification={notification}
              onOpen={
                matterId
                  ? () => {
                      toast.dismiss(id);
                      if (!notification.readAt) {
                        markNotificationRead({
                          id: notification.id,
                          read: true,
                          silent: true,
                        });
                      }
                      void queryClient.invalidateQueries({
                        queryKey: [queryKeys.matters, matterId],
                      });
                      void navigate({ to: `/matters/${matterId}` });
                    }
                  : undefined
              }
              onReply={
                replyTarget
                  ? () => {
                      toast.dismiss(id);
                      if (!notification.readAt) {
                        markNotificationRead({
                          id: notification.id,
                          read: true,
                          silent: true,
                        });
                      }
                      openCommunicationModal(replyTarget);
                    }
                  : undefined
              }
            />
          ),
          {
            id,
            style: {
              borderColor: `${visual.color}52`,
              width: "420px",
              maxWidth: "100%",
              alignItems: "flex-start",
            },
            icon: (
              <span
                className="mt-1 flex size-5 shrink-0 items-center justify-center rounded-full text-white"
                style={{ background: visual.color }}
              >
                <Icon className="size-3" aria-hidden="true" />
              </span>
            ),
          },
        );
      });
    },
    [
      canSendSms,
      markNotificationRead,
      navigate,
      openCommunicationModal,
      queryClient,
    ],
  );
};
